/* Wire shapes of the member-callable surface, ported from the desktop's
 * hosted-primary.ts so both clients accept the same payloads. Field names are
 * camelCase projections of the RPC JSON. */

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export const HOSTED_PROFILE_ROLES = ["owner", "admin", "contributor", "viewer"] as const;
export type HostedProfileRole = (typeof HOSTED_PROFILE_ROLES)[number];

export const HOSTED_SESSION_EVENT_TYPES = [
	"message",
	"session_info",
	"run_receipt",
	"artifact_receipt",
	"authorization_request",
	"authorization_decision",
	"interaction_request",
	"interaction_answer",
] as const;
export type HostedSessionEventType = (typeof HOSTED_SESSION_EVENT_TYPES)[number];

export interface HostedProfile {
	profileId: string;
	environmentId: string;
	profileName: string;
	environmentName: string;
	role: HostedProfileRole;
	contractVersion: number;
	createdAt: string;
}

export const HOSTED_FRIEND_STATUSES = ["pending", "accepted"] as const;
export type HostedFriendStatus = (typeof HOSTED_FRIEND_STATUSES)[number];

/* A friendship as `qorpera_list_friends` reports it. Direction is relative to
 * the caller: `incoming` means the other account asked and this account
 * answers. Company invitations only reach people you are already friends
 * with, which is why the account menu carries this list. */
export interface HostedFriend {
	userId: string;
	email: string;
	displayName: string;
	status: HostedFriendStatus;
	direction: "incoming" | "outgoing";
	since: string;
}

/* The account `qorpera_find_account_by_email` found for a whole address. The
 * Friends search shows a person to add only when an account has confirmed the
 * address typed; `self` marks the caller's own address. */
export interface HostedAccountMatch {
	userId: string;
	email: string;
	displayName: string;
	self: boolean;
}

export interface HostedInvitationDelivery {
	status: string;
	attemptCount: number;
	nextAttemptAt: string;
	providerMessageId?: string;
	lastError?: string;
	sentAt?: string;
}

export interface HostedProfileInvitation {
	id: string;
	profileId: string;
	email: string;
	role: Exclude<HostedProfileRole, "owner">;
	invitedByUserId: string;
	createdAt: string;
	expiresAt: string;
	delivery?: HostedInvitationDelivery;
}

export interface HostedIncomingProfileInvitation extends HostedProfileInvitation {
	environmentId: string;
	profileName: string;
	environmentName: string;
}

export interface HostedProfileMember {
	userId: string;
	email: string;
	displayName: string;
	avatarUrl?: string;
	role: HostedProfileRole;
	joinedAt: string;
}

export interface HostedProfileTeam {
	profileId: string;
	role: HostedProfileRole;
	capabilities: {
		read: true;
		write: boolean;
		authorize: boolean;
		manageMembers: boolean;
		/** Founders make founders (2026-09-28). Absent from a cloud before several founders. */
		manageFounders?: boolean;
		/** Whether this member may leave: not the last member, and not the last founder while others stay. */
		leave?: boolean;
		/** Only the last member deletes the company. */
		deleteCompany?: boolean;
	};
	members: HostedProfileMember[];
	invitations: HostedProfileInvitation[];
}

export type HostedSessionAudience = { kind: "company" } | { kind: "member_private"; userId: string };

export interface HostedSession {
	sessionId: string;
	clientSessionId: string;
	profileId: string;
	environmentId: string;
	title: string;
	staffAgentId?: string;
	parentSessionId?: string;
	executionScope?: Record<string, JsonValue>;
	staffVersion?: number;
	audience: HostedSessionAudience;
	status: "open" | "closed";
	createdByUserId: string;
	createdAt: string;
	updatedAt: string;
}

export type HostedSessionExecutionIdentity =
	| { kind: "turn" | "wake"; id: string; claimantId: string }
	| { kind: "run"; id: string; claimantId: string; runnerAssignmentId: string };

export interface HostedStaffSpeaker {
	runId: string;
	staffAgentId: string;
	staffVersion: number;
	/** Name captured at send time; absent when historical version cannot be established. */
	displayName?: string;
}

export interface HostedSessionEvent {
	sessionId: string;
	sequence: number;
	eventId: string;
	eventType: HostedSessionEventType;
	actorUserId: string;
	body: JsonValue;
	previousHash?: string;
	eventHash: string;
	execution?: HostedSessionExecutionIdentity;
	staffSpeaker?: HostedStaffSpeaker;
	sessionInputId?: string;
	createdAt: string;
}

export interface StaffSessionHandle {
	sessionId: string;
	clientSessionId: string;
	environmentId: string;
	title: string;
	staffVersion?: number;
	audience: HostedSessionAudience;
	created: boolean;
	createdByUserId: string;
	createdAt: string;
}

export interface PendingSessionInput {
	inputId: string;
	position: number;
	kind: "kickoff" | "user_message";
	authorUserId: string;
	authorDisplayName: string;
	body: JsonValue;
	createdAt: string;
	status: "queued" | "processing" | "failed";
	failureReason?: string;
}

export interface PendingSessionInputPage {
	items: PendingSessionInput[];
	nextAfterPosition: number;
	hasMore: boolean;
}

export interface SessionInputReceipt {
	inputId: string;
	position: number;
}

export interface HostedStaffAgent {
	profileId: string;
	id: string;
	displayName: string;
	role: string;
	charter: string;
	templateId?: string;
	departmentId?: string;
	appearance: { color: string; shape: string; creature?: string };
	model?: { provider: string; modelId: string };
	employmentStatus: "active" | "archived";
	lifecycleState: "setup" | "active";
	version: number;
	createdAt: string;
	updatedAt: string;
	updatedByUserId: string;
}

export const EXECUTION_SOURCE_KINDS = [
	"session_turn",
	"wake",
	"manual",
	"room_delivery",
	"proposal_review",
	"system",
] as const;
export type ExecutionSourceKind = (typeof EXECUTION_SOURCE_KINDS)[number];

export interface HostedStaffExecutionActivity {
	runId: string;
	staffAgentId: string;
	sourceKind: ExecutionSourceKind;
	jobStatus: "queued" | "leased" | "completed" | "failed" | "cancelled";
	activity: "waiting" | "working" | "needs_attention";
	/** Expiring presentation hint; it never changes durable execution state. */
	phase?: "working" | "writing";
	/** A working run's last progress, when that is noProgressMinutes old (session-buildup audit gap 4, migration
	 * 20260930002600). The run goes on; nothing stops it. The cloud sets both or neither. */
	noProgressSince?: string;
	noProgressMinutes?: number;
	/** A failure from before the agent's newer work (migration 20260930542000): listed apart from the agent's current
	 * row, never its state. Only a failure is. */
	superseded?: true;
	updatedAt: string;
}

/** A worker's own run, for a member's Stop (qorpera_get_worker_run_control; gap 4b, 20260930002800). */
export interface WorkerRunControl {
	runId: string;
	status: "queued" | "leased" | "completed" | "failed" | "cancelled";
	canCancel: boolean;
}

/** A failed or stopped run nobody has dismissed yet (qorpera_get_execution_run_recovery). */
export interface HostedRunRecovery {
	runId: string;
	sourceKind: ExecutionSourceKind;
	jobStatus: "failed" | "cancelled";
	lastError?: string;
	/** An open Company File proposal the run left behind: it waits on a decision. */
	proposalId?: string;
	/** The model account paying for the run reached its usage limit during it (20260929910000). */
	usageLimit?: boolean;
	/** A member's ChatGPT plan pays for the run (20260929993000); absent from an older cloud. */
	paidByChatgptPlan?: boolean;
	/** The member the run was started for, whose login pays for it. */
	requestedByUserId?: string;
	/** The sponsor binding (the login) that paid for the run: the member's own logins name theirs, so the web can tell
	 * whether it is the reading member's own and when its usage limit resets. */
	sponsorBindingId?: string;
	/** Operations still executing or of unknown outcome. The cloud refuses to
	 * dismiss the run until they settle. */
	unsettledOperations: number;
	/** An operation of the run completed, or its outcome is unknown: the cloud refuses to try the run again
	 * (qorpera_resolve_failed_execution_run), since that could repeat the work. */
	durableOutcome?: boolean;
}

export interface HostedAccountEntitlement {
	planKey: string;
	billingMode: string;
	featureSet: string;
	storageAllocation: string;
	storageAllocationBytes?: number;
	source: string;
	updatedAt?: string;
}

export interface HostedCompanyFile {
	profileId: string;
	environmentId: string;
	storageUsedBytes: number;
	storageLimitBytes: number;
	entitlement?: HostedAccountEntitlement;
	createdAt: string;
	updatedAt: string;
}
